import { useCallback, useEffect, useMemo } from "react"

import { GridColDef, GridSortModel } from "@mui/x-data-grid"
import { Trans } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { HelpOutlineRoundedIcon } from "@l2r-front/l2r-icons"
import { useNetworksStateContext, useRoleIsReadOnly } from "@l2r-front/l2r-networks"
import { Campaign } from "@l2r-front/l2r-proptypes"
import { CatalogItemsCount, IconButton, L2RLink, L2RTable, MoreMenu, Tooltip } from "@l2r-front/l2r-ui"

import { APP_CITY } from "../../../../common/constants/appCity"
import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { API_MODULE_HORIZONTAL_SIGNING, MODULE_HORIZONTAL_SIGNING } from "../../../../common/constants/modules"
import { useHorizontalSigningCampaignTable } from "../../hooks/queries/horizontalSigning/useHorizontalSigningCampaignTable"

import * as Styled from "./HorizontalSigningCampaignTable.styled"

const DEFAULT_SORT_FIELDS = ["network", "road", "horizontal_sign_type_name", "identifier"]

interface HorizontalSigningCampaignTableProps {
    campaign: Campaign
    sortModel?: GridSortModel
    sortHistory?: string[]
    onSortChange?: (_sortModel: GridSortModel, _sortHistory: string[]) => void
}

export function HorizontalSigningCampaignTable(props: HorizontalSigningCampaignTableProps) {
    const { campaign, sortModel, sortHistory, onSortChange } = props
    const { t } = useTranslation(I18N_NAMESPACE)
    const { selectedNetwork, selectedSubNetworkSlugs } = useNetworksStateContext()
    const { data } = useHorizontalSigningCampaignTable({ campaign: campaign?.uuid })
    const navigate = useNavigate()
    const { search } = useLocation()
    const readOnly = useRoleIsReadOnly(selectedSubNetworkSlugs, API_MODULE_HORIZONTAL_SIGNING)

    const onNavigateToDetails = useCallback((row) => {
        navigate(encodeURI(`/${selectedNetwork?.slug}/${APP_CITY}/${MODULE_HORIZONTAL_SIGNING}/${row.road}/${row.uuid}`))
    }, [navigate, selectedNetwork?.slug])

    const columns: GridColDef[] = [
        {
            field: "identifier",
            headerName: t(I18N_NAMESPACE, "components.horizontalSigningCampaignTable.header.identifier"),
            editable: false,
            align: "center",
            headerAlign: "center",
        },
        {
            field: "horizontal_sign_type_name",
            headerName: t(I18N_NAMESPACE, "components.horizontalSigningCampaignTable.header.signName"),
            editable: false,
            align: "center",
            headerAlign: "center",
        },
        {
            field: "road",
            headerName: t(I18N_NAMESPACE, "components.horizontalSigningCampaignTable.header.road"),
            editable: false,
            align: "center",
            headerAlign: "center",
        },
        {
            field: "network",
            headerName: t(I18N_NAMESPACE, "components.horizontalSigningCampaignTable.header.network"),
            editable: false,
            align: "center",
            headerAlign: "center",
        },
        {
            field: "position",
            headerName: t(I18N_NAMESPACE, "components.horizontalSigningCampaignTable.header.position"),
            editable: false,
            sortable: false,
            align: "center",
            headerAlign: "center",
        },
        {
            field: "actions",
            headerName: "",
            editable: false,
            sortable: false,
            align: "center",
            headerAlign: "center",
            width: 60,
            renderCell: (params) => (
                params.row.disabled ? null : <MoreMenu
                    color="tertiary"
                    menuConfig={[
                        {
                            id: "edit",
                            label: "Détails",
                            props: {
                                onClick: () => onNavigateToDetails(params.row),
                            },
                        },
                    ]}
                />
            ),
        },
    ]

    const rows = useMemo(() => {
        if (!data) return null
        return data.map((row) => {
            const disabled = !row.has_access ||
                (selectedNetwork.children?.length && !row.sub_network_slugs.some(slug => selectedSubNetworkSlugs.includes(slug)))
            const subNetworksNames = row.sub_network_slugs
                .filter(slug => selectedNetwork.children?.some(child => child.slug === slug))
                .map(slug => selectedNetwork.children?.find(child => child.slug === slug)?.name).filter(Boolean)

            return {
                ...row,
                disabled,
                id: row.uuid,
                network: disabled ? t("l2r-common", "offNetwork") : subNetworksNames.length ?
                    subNetworksNames.join(", ") : selectedNetwork.name,
                position: `${row.geometry.coordinates[1].toFixed(5)}, ${row.geometry.coordinates[0].toFixed(5)}`,
            }
        })
    }, [data, selectedNetwork, selectedSubNetworkSlugs, t])

    useEffect(() => {
        if (onSortChange && !sortHistory?.length) {
            onSortChange([{ field: DEFAULT_SORT_FIELDS[0], sort: "asc" }], DEFAULT_SORT_FIELDS)
        }
    }, [sortHistory, onSortChange])

    const emptyText = useMemo(() => {
        return readOnly ?
            t(I18N_NAMESPACE, "containers.horizontalSignTypeList.emptyReadOnly")
            :
            <Trans
                ns={I18N_NAMESPACE}
                i18nKey="containers.horizontalSignTypeList.empty"
                components={[
                    <Styled.InfoLink to={`/${selectedNetwork.slug}/${APP_CITY}/${MODULE_HORIZONTAL_SIGNING}/`} />]}
            />
    }, [selectedNetwork.slug, readOnly, t])

    if (!data) {
        return null
    }

    return <Styled.Wrapper>
        <Styled.Header>
            <L2RLink color="inherit" to={`/${selectedNetwork?.slug}/${APP_CITY}/${MODULE_HORIZONTAL_SIGNING}/${search}`} underline="none">
                <Styled.LinkText variant="Desktop & Mobile/Heading/H2">
                    {t(I18N_NAMESPACE, "containers.horizontalSignTypeList.title")}
                </Styled.LinkText>
            </L2RLink>
            {(!readOnly && rows.length > 0) &&
                <Tooltip disableFocusListener title={t(I18N_NAMESPACE, "containers.horizontalSignTypeList.tooltip")}>
                    <IconButton size="small">
                        <HelpOutlineRoundedIcon color="action/disabled" />
                    </IconButton>
                </Tooltip>
            }
            <CatalogItemsCount filteredCount={rows.length} isMainCount />
        </Styled.Header>
        <L2RTable
            rows={rows}
            columns={columns}
            pageSizeOptions={[5]}
            disableRowSelectionOnClick
            defaultSortFields={DEFAULT_SORT_FIELDS}
            sortModel={sortModel}
            sortHistory={sortHistory}
            onSortChange={onSortChange}
            emptyText={emptyText}
        />
    </Styled.Wrapper>
}